import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import MarkdownCodeEditor from '../../../shared/components/MarkdownCodeEditor';
import type { PromptTypeType } from '../../../types/backend';
import { detectTemplateVariables } from '../utils/promptUtils';

interface PromptContentEditorProps {
  content: string;
  onChange: (content: string) => void;
  contentError: string | null;
  viewingVersion: string | null;
  viewingContent: string;
  versionLoading: boolean;
  promptType: PromptTypeType;
  /** What describes the prompt, shown above it and switching with it. */
  description?: { text: string; field?: React.ReactNode };
}

const PromptContentEditor: React.FC<PromptContentEditorProps> = ({
  content,
  onChange,
  contentError,
  viewingVersion,
  viewingContent,
  versionLoading,
  promptType,
  description,
}) => {
  const isViewingHistory = viewingVersion !== null;
  const isEnrichment = promptType === 'enrichment';
  const missingEventPlaceholder = isEnrichment && content.length > 0 && !content.includes('{event}');

  const detectedVariables = useMemo(() => detectTemplateVariables(content), [content]);
  const variablesHint = detectedVariables.length > 0 ? `Template variables: ${detectedVariables.join(', ')}` : undefined;

  if (isViewingHistory) {
    return (
      <Box sx={{ px: 3, pt: 2 }}>
        <InlineAlert severity="info" sx={{ mb: 1 }}>
          Viewing published v{viewingVersion} (read-only). Select &quot;Draft&quot; to resume editing.
        </InlineAlert>
        <MarkdownCodeEditor
          value={versionLoading ? 'Loading…' : viewingContent}
          onChange={() => {}}
          readOnly
          minHeight={360}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, pt: 2 }}>
      {isEnrichment && (
        <InlineAlert severity={missingEventPlaceholder ? 'warning' : 'info'} sx={{ mb: 1 }}>
          Enrichment prompts must include the <code>{'{event}'}</code> placeholder — it is
          replaced with the triggering event payload before the model runs.
        </InlineAlert>
      )}
      <MarkdownCodeEditor
        label="Prompt"
        value={content}
        onChange={onChange}
        minHeight={360}
        preamble={description}
      />
      {(contentError || variablesHint) && (
        <Typography
          variant="caption"
          color={contentError ? 'error.main' : 'text.secondary'}
          sx={{ mt: 0.5, display: 'block' }}
        >
          {contentError || variablesHint}
        </Typography>
      )}
    </Box>
  );
};

export default PromptContentEditor;
